/**
 * Binds every route in the table to a real screen.
 *
 * Exhaustive by construction: the map is typed `Record<RoutePath, ...>`, so adding a
 * route without a screen fails the build rather than silently falling back to a
 * placeholder. A deep link from a notification, a widget or a share must never land on
 * a blank page.
 */

import { useNavigate, useParams } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useApp } from './app-context.js';
import type { RoutePath } from './router.js';
import {
  ConversationReportRoute,
  LessonSessionRoute,
  OnboardingRoute,
  PastReportRoute,
  ReportRoute,
  ReviewSessionRoute,
  SessionTranscriptRoute,
  TodayRoute,
} from './screens/containers.js';
import {
  ConjugationScreen,
  GrammarConceptScreen,
  GrammarListScreen,
  LibraryScreen,
  ListeningItemScreen,
  ListenListScreen,
  SavedItemsScreen,
  WordDeckScreen,
  WordDetailScreen,
  WordGameScreen,
  WordsScreen,
  WorkbookScreen,
} from './screens/content.js';
import {
  DrillScreen,
  LearnHubScreen,
  LearnPathScreen,
  LessonDetailScreen,
  ModuleScreen,
  PlacementScreen,
} from './screens/learn.js';
import { JoinSessionRoute, LiveSessionRoute } from './screens/live-route.js';
import {
  AchievementsScreen,
  AssessmentScreen,
  CertificateScreen,
  LeaguesScreen,
  LevelScreen,
  ProgressScreen,
  ReportsScreen,
  SettingsScreen,
} from './screens/me.js';
import { ClinicScreen, MistakesScreen, PlanComparisonScreen } from './screens/monetization.js';
import { ReviewHubScreen } from './screens/review-hub.js';
import {
  DebateScreen,
  InterviewScreen,
  OpenTalkScreen,
  RoleplayBuilderScreen,
  ScenarioScreen,
  SharedScenarioScreen,
  TalkHubScreen,
  TextingScenarioScreen,
  TutorPickerScreen,
} from './screens/talk.js';
import { DailyChallengeScreen } from './screens/today.js';

/**
 * One navigation function for every screen.
 *
 * Screens emit the same path strings iOS and Android use, rather than TanStack's typed
 * route union, so a route string in a notification payload or a `FreeAlternative`
 * works identically on all three platforms.
 */
export function useNavigateTo(): (route: string) => void {
  const navigate = useNavigate();
  return (route: string) => {
    void navigate({ to: route });
  };
}

function useParam(name: string): string {
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  return params[name] ?? '';
}

// Each route is a real component so hooks are called unconditionally at the top level.

function ConjugationPage(): JSX.Element {
  return <ConjugationScreen onNavigate={useNavigateTo()} />;
}
function SavedPage(): JSX.Element {
  return <SavedItemsScreen onNavigate={useNavigateTo()} />;
}
function DebatePage(): JSX.Element {
  return <DebateScreen onNavigate={useNavigateTo()} />;
}
function InterviewPage(): JSX.Element {
  return <InterviewScreen onNavigate={useNavigateTo()} />;
}
function TextingPage(): JSX.Element {
  return <TextingScenarioScreen scenarioId={useParam('scenarioId')} onNavigate={useNavigateTo()} />;
}
function SharedScenarioPage(): JSX.Element {
  return <SharedScenarioScreen shareToken={useParam('shareToken')} onNavigate={useNavigateTo()} />;
}
function ConversationReportPage(): JSX.Element {
  return <ConversationReportRoute sessionId={useParam('sessionId')} onNavigate={useNavigateTo()} />;
}

function ChallengePage(): JSX.Element {
  return <DailyChallengeScreen onNavigate={useNavigateTo()} />;
}
function LeaguesPage(): JSX.Element {
  return <LeaguesScreen onNavigate={useNavigateTo()} />;
}
function CertificatePage(): JSX.Element {
  return <CertificateScreen onNavigate={useNavigateTo()} />;
}
function AssessmentPage(): JSX.Element {
  return <AssessmentScreen onNavigate={useNavigateTo()} />;
}
function PlansPage(): JSX.Element {
  const navigate = useNavigateTo();
  return (
    <PlanComparisonScreen
      // Opened as a page rather than from inside a paywall, which the table reports
      // so its own conversion can be told apart from the paywall's.
      source="settings"
      onSelect={() => navigate('/me/settings')}
    />
  );
}

function TodayPage(): JSX.Element {
  return <TodayRoute onNavigate={useNavigateTo()} />;
}
function OnboardingPage(): JSX.Element {
  return <OnboardingRoute onNavigate={useNavigateTo()} />;
}
function LearnHubPage(): JSX.Element {
  return <LearnHubScreen onNavigate={useNavigateTo()} />;
}
function LearnPathPage(): JSX.Element {
  return <LearnPathScreen onNavigate={useNavigateTo()} />;
}
function ModulePage(): JSX.Element {
  return <ModuleScreen moduleId={useParam('moduleId')} onNavigate={useNavigateTo()} />;
}
function LessonDetailPage(): JSX.Element {
  return <LessonDetailScreen lessonId={useParam('lessonId')} onNavigate={useNavigateTo()} />;
}
function LessonSessionPage(): JSX.Element {
  return <LessonSessionRoute lessonId={useParam('lessonId')} onNavigate={useNavigateTo()} />;
}
function ReportPage(): JSX.Element {
  return <ReportRoute sessionId={useParam('sessionId')} onNavigate={useNavigateTo()} />;
}
function DrillPage(): JSX.Element {
  return <DrillScreen drillId={useParam('drillId')} onNavigate={useNavigateTo()} />;
}
function WordsPage(): JSX.Element {
  return <WordsScreen onNavigate={useNavigateTo()} />;
}
function WordDeckPage(): JSX.Element {
  return <WordDeckScreen onNavigate={useNavigateTo()} />;
}
function WordDetailPage(): JSX.Element {
  return <WordDetailScreen itemId={useParam('itemId')} onNavigate={useNavigateTo()} />;
}
function WordGamePage(): JSX.Element {
  return <WordGameScreen gameType={useParam('gameType')} onNavigate={useNavigateTo()} />;
}
function GrammarListPage(): JSX.Element {
  return <GrammarListScreen onNavigate={useNavigateTo()} />;
}
function GrammarConceptPage(): JSX.Element {
  return <GrammarConceptScreen conceptId={useParam('conceptId')} onNavigate={useNavigateTo()} />;
}
function ListenListPage(): JSX.Element {
  return <ListenListScreen onNavigate={useNavigateTo()} />;
}
function ListeningItemPage(): JSX.Element {
  return <ListeningItemScreen itemId={useParam('itemId')} onNavigate={useNavigateTo()} />;
}
function PlacementPage(): JSX.Element {
  return <PlacementScreen onNavigate={useNavigateTo()} />;
}
function TalkHubPage(): JSX.Element {
  return <TalkHubScreen onNavigate={useNavigateTo()} />;
}
function ScenarioPage(): JSX.Element {
  return <ScenarioScreen scenarioId={useParam('scenarioId')} onNavigate={useNavigateTo()} />;
}
function ScenarioSessionPage(): JSX.Element {
  return <LiveSessionRoute kind="scenario" refId={useParam('scenarioId')} />;
}
function OpenTalkSessionPage(): JSX.Element {
  return <LiveSessionRoute kind="open" refId={null} />;
}
function JoinSessionPage(): JSX.Element {
  return <JoinSessionRoute sessionId={useParam('sessionId')} />;
}
function OpenTalkPage(): JSX.Element {
  return <OpenTalkScreen onNavigate={useNavigateTo()} />;
}
function RoleplayPage(): JSX.Element {
  return <RoleplayBuilderScreen onNavigate={useNavigateTo()} />;
}
function TutorsPage(): JSX.Element {
  return <TutorPickerScreen onNavigate={useNavigateTo()} />;
}
function ReviewHubPage(): JSX.Element {
  return <ReviewHubScreen onNavigate={useNavigateTo()} />;
}
function ReviewSessionPage(): JSX.Element {
  return <ReviewSessionRoute onNavigate={useNavigateTo()} />;
}
function MistakesPage(): JSX.Element {
  return <MistakesScreen onNavigate={useNavigateTo()} />;
}
function ClinicPage(): JSX.Element {
  return <ClinicScreen onNavigate={useNavigateTo()} />;
}
function SoundDrillPage(): JSX.Element {
  // Decoded, because a phoneme is IPA and `/ʎ/` arrives percent-encoded.
  return (
    <ClinicScreen onNavigate={useNavigateTo()} phoneme={decodeURIComponent(useParam('phoneme'))} />
  );
}
function ProgressPage(): JSX.Element {
  return <ProgressScreen onNavigate={useNavigateTo()} />;
}
function LevelPage(): JSX.Element {
  return <LevelScreen onNavigate={useNavigateTo()} />;
}
function ReportsPage(): JSX.Element {
  return <ReportsScreen onNavigate={useNavigateTo()} />;
}
function PastReportPage(): JSX.Element {
  return <PastReportRoute sessionId={useParam('sessionId')} onNavigate={useNavigateTo()} />;
}
function SessionTranscriptPage(): JSX.Element {
  return <SessionTranscriptRoute sessionId={useParam('sessionId')} onNavigate={useNavigateTo()} />;
}
function SettingsPage(): JSX.Element {
  return <SettingsScreen onNavigate={useNavigateTo()} />;
}
function LibraryPage(): JSX.Element {
  return <LibraryScreen onNavigate={useNavigateTo()} />;
}
function WorkbookPage(): JSX.Element {
  return <WorkbookScreen onNavigate={useNavigateTo()} />;
}

/**
 * Screens that can be reached without a course, and must not be guarded.
 *
 * Today already renders onboarding when there is no course; onboarding obviously needs
 * no course to run.
 */
const OPEN_WITHOUT_COURSE: ReadonlySet<RoutePath> = new Set(['/', '/onboarding/$step']);

/**
 * Sends a learner with no course to onboarding instead of throwing.
 *
 * Nearly every screen asks for the active course with `useCourseId()`, which throws when
 * there is none — a programming invariant, and the right rule for a screen that cannot
 * work without one. What was missing was anything that stopped the router getting there:
 * a tap on the tab bar during onboarding, or a notification or share link opened before
 * setup, replaced the whole app with TanStack's "Something went wrong!".
 *
 * Guarding the table rather than each screen, because this is a routing rule and there
 * are twenty-nine call sites. `screens.test.tsx` holds every route to it.
 */
function requireCourse(path: RoutePath, Page: () => JSX.Element): () => JSX.Element {
  if (OPEN_WITHOUT_COURSE.has(path)) return Page;
  return function Guarded(): JSX.Element {
    const { courseId } = useApp();
    const navigate = useNavigateTo();
    if (courseId === null) return <OnboardingRoute onNavigate={navigate} />;
    return <Page />;
  };
}

const SCREENS: Record<RoutePath, () => JSX.Element> = {
  '/': TodayPage,
  '/onboarding/$step': OnboardingPage,

  '/learn': LearnHubPage,
  '/learn/path': LearnPathPage,
  '/learn/module/$moduleId': ModulePage,
  '/learn/lesson/$lessonId': LessonDetailPage,
  '/learn/lesson/$lessonId/session': LessonSessionPage,
  '/learn/lesson/$lessonId/report/$sessionId': ReportPage,
  '/learn/drill/$drillId': DrillPage,
  '/learn/words': WordsPage,
  '/learn/words/deck': WordDeckPage,
  '/learn/words/$itemId': WordDetailPage,
  '/learn/words/games/$gameType': WordGamePage,
  '/learn/grammar': GrammarListPage,
  '/learn/grammar/conjugation': ConjugationPage,
  '/learn/grammar/$conceptId': GrammarConceptPage,
  '/learn/listen': ListenListPage,
  '/learn/listen/$itemId': ListeningItemPage,
  '/learn/placement': PlacementPage,
  '/learn/challenge': ChallengePage,

  '/talk': TalkHubPage,
  '/talk/scenarios': TalkHubPage,
  '/talk/scenario/$scenarioId': ScenarioPage,
  '/talk/scenario/$scenarioId/session': ScenarioSessionPage,
  '/talk/open': OpenTalkPage,
  '/talk/open/session': OpenTalkSessionPage,
  '/talk/session/$sessionId': JoinSessionPage,
  '/talk/texting/$scenarioId': TextingPage,
  '/talk/scenario/$scenarioId/report/$sessionId': ConversationReportPage,
  '/shared/$shareToken': SharedScenarioPage,
  '/talk/debate': DebatePage,
  '/talk/interview': InterviewPage,
  '/talk/roleplay/new': RoleplayPage,
  '/talk/tutors': TutorsPage,

  '/review': ReviewHubPage,
  '/review/session': ReviewSessionPage,
  '/review/mistakes': MistakesPage,
  '/review/clinic': ClinicPage,
  '/review/clinic/$phoneme': SoundDrillPage,

  '/me': ProgressPage,
  '/me/level': LevelPage,
  '/me/reports': ReportsPage,
  '/me/reports/$sessionId': PastReportPage,
  '/me/reports/$sessionId/transcript': SessionTranscriptPage,
  '/me/achievements': AchievementsScreen,
  '/me/saved': SavedPage,
  '/me/leagues': LeaguesPage,
  '/me/certificate': CertificatePage,
  '/me/assessment': AssessmentPage,
  '/me/plans': PlansPage,
  '/me/settings': SettingsPage,

  '/library': LibraryPage,
  '/workbook': WorkbookPage,
};

export const ROUTE_COMPONENTS: Record<RoutePath, () => JSX.Element> = Object.fromEntries(
  Object.entries(SCREENS).map(([path, Page]) => [path, requireCourse(path as RoutePath, Page)]),
) as Record<RoutePath, () => JSX.Element>;
